import React, { useState, useEffect } from 'react';
import {
  X,
  Building2,
  Phone,
  Mail,
  MapPin,
  FileText,
  Check,
  ShieldCheck,
  KeyRound,
  Lock,
  User,
  Users,
  UserCheck,
  Smartphone,
  Plus,
  Trash2,
  Upload,
  Image as ImageIcon,
  MessageSquare,
  Sparkles,
  RefreshCw,
  Send,
  AlertTriangle,
  HelpCircle,
  Edit2,
  UserPlus,
  CheckCircle2,
  XCircle,
  Wrench,
  Info,
  Tag,
  Search,
  RotateCcw,
  Eye,
  EyeOff,
  Copy,
  FileCode,
  Database,
  HardDrive,
  Download,
  Layers,
  Table,
  ExternalLink,
  AlertCircle,
} from 'lucide-react';
import { DatabaseTableMonitor } from './DatabaseTableMonitor';
import { AppUser, CompanyInfo, SmsSettings, UserRole } from '../types';
import {
  getLocalSmsSettings,
  saveLocalSmsSettings,
  saveServerSmsSettings,
  checkSmsBalance,
  sendSmsMessage,
  DEFAULT_SMS_SETTINGS,
} from '../utils/smsService';
import {
  getStoredUsers,
  saveUsers,
  getCurrentUser,
  setCurrentUser,
  setCurrentAuthVersion,
  getStoredDeviceTypes,
  saveDeviceTypes,
  getStoredDeviceBrands,
  saveDeviceBrands,
  downloadServerDataJson,
  getFullDatabaseData,
  restoreFullDatabaseData,
  testMySqlConnection,
  getMySqlConfig,
} from '../utils/storage';
import { downloadSqlDump } from '../utils/sqlExporter';
import { defaultDeviceTypes, defaultDeviceBrands } from '../data/initialData';

interface CompanySettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyInfo: CompanyInfo;
  onSave: (info: CompanyInfo) => void;
  deviceTypes?: string[];
  onSaveDeviceTypes?: (types: string[]) => void;
  deviceBrands?: string[];
  onSaveDeviceBrands?: (brands: string[]) => void;
  onOpenDeviceTypesModal?: () => void;
  initialTab?: 'company' | 'sms' | 'security' | 'users' | 'devices' | 'database';
}

export const CompanySettingsModal: React.FC<CompanySettingsModalProps> = (props) => {
  if (!props.isOpen) return null;
  return <CompanySettingsModalContent {...props} />;
};

const CompanySettingsModalContent: React.FC<CompanySettingsModalProps> = ({
  isOpen,
  onClose,
  companyInfo,
  onSave,
  deviceTypes,
  onSaveDeviceTypes,
  deviceBrands,
  onSaveDeviceBrands,
  initialTab = 'company',
}) => {
  const [activeTab, setActiveTab] = useState<'company' | 'sms' | 'security' | 'users' | 'devices' | 'database'>(initialTab);

  // Device types & brands state
  const [deviceTypesList, setDeviceTypesList] = useState<string[]>(
    deviceTypes && deviceTypes.length > 0 ? deviceTypes : getStoredDeviceTypes()
  );
  const [deviceBrandsList, setDeviceBrandsList] = useState<string[]>(
    deviceBrands && deviceBrands.length > 0 ? deviceBrands : getStoredDeviceBrands()
  );
  const [newTypeName, setNewTypeName] = useState('');
  const [newBrandName, setNewBrandName] = useState('');
  const [brandSearchQuery, setBrandSearchQuery] = useState('');
  const [deviceSuccessMsg, setDeviceSuccessMsg] = useState<string | null>(null);
  const [deviceErrorMsg, setDeviceErrorMsg] = useState<string | null>(null);

  // Users State
  const [usersList, setUsersList] = useState<AppUser[]>(getStoredUsers());
  const [editingUserId, setEditingUserId] = useState<string | null>(null); // 'new' or id
  const [userFormName, setUserFormName] = useState('');
  const [userFormUsername, setUserFormUsername] = useState('');
  const [userFormPassword, setUserFormPassword] = useState('');
  const [userFormPhone, setUserFormPhone] = useState('');
  const [userFormRole, setUserFormRole] = useState<UserRole>('technician');
  const [userFormIsActive, setUserFormIsActive] = useState<boolean>(true);
  const [userFormError, setUserFormError] = useState<string | null>(null);
  const [userSuccessMessage, setUserSuccessMessage] = useState<string | null>(null);

  // Company Info State
  const [name, setName] = useState(companyInfo.name);
  const [phone, setPhone] = useState(companyInfo.phone);
  const [email, setEmail] = useState(companyInfo.email);
  const [address, setAddress] = useState(companyInfo.address);
  const [logoUrl, setLogoUrl] = useState(companyInfo.logoUrl || '');
  const [logoError, setLogoError] = useState<string | null>(null);
  const [taxOffice, setTaxOffice] = useState(companyInfo.taxOffice || '');
  const [taxNumber, setTaxNumber] = useState(companyInfo.taxNumber || '');
  const [terms, setTerms] = useState(companyInfo.terms);
  const [deviceAreaAssignments, setDeviceAreaAssignments] = useState<Record<string, 'printer' | 'computer'>>(
    companyInfo.deviceAreaAssignments || {}
  );
  const [adminUsername, setAdminUsername] = useState(companyInfo.adminUsername || 'admin');
  const [adminPassword, setAdminPassword] = useState('');
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [securityQuestion, setSecurityQuestion] = useState(companyInfo.securityQuestion || 'İlk evcil hayvanınızın adı nedir?');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [showSecurityAnswer, setShowSecurityAnswer] = useState(false);
  const [visibleUserPasswordIds, setVisibleUserPasswordIds] = useState<Record<string, boolean>>({});
  const [copiedNotice, setCopiedNotice] = useState<string | null>(null);
  const [copiedPortalSettingsLink, setCopiedPortalSettingsLink] = useState<boolean>(false);

  // SMS Settings State
  const [smsSettings, setSmsSettings] = useState<SmsSettings>(getLocalSmsSettings());
  const [smsBalance, setSmsBalance] = useState<string | null>(null);
  const [checkingBalance, setCheckingBalance] = useState<boolean>(false);
  const [testPhone, setTestPhone] = useState<string>('');
  const [testSmsStatus, setTestSmsStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [sendingTestSms, setSendingTestSms] = useState<boolean>(false);
  const [savingSms, setSavingSms] = useState<boolean>(false);
  const [smsSavedNotice, setSmsSavedNotice] = useState<boolean>(false);

  // Backup & Restore State
  const [restoringBackup, setRestoringBackup] = useState<boolean>(false);
  const [restoreMessage, setRestoreMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [downloadingSql, setDownloadingSql] = useState<boolean>(false);

  // Database Connection Test State (Requested by user)
  const [isTestingDb, setIsTestingDb] = useState<boolean>(false);
  const [dbTestResult, setDbTestResult] = useState<{
    success: boolean;
    title: string;
    message: string;
    serverVersion?: string;
    database?: string;
    tablesCount?: number;
    pingMs?: number;
    troubleshooting?: string;
    time?: string;
  } | null>(null);

  const handleTestDatabaseConnection = async () => {
    setIsTestingDb(true);
    setDbTestResult(null);
    try {
      const cfg = await getMySqlConfig();
      const res = await testMySqlConnection(cfg);
      const timeStr = new Date().toLocaleTimeString('tr-TR');
      if (res.success) {
        setDbTestResult({
          success: true,
          title: 'Bağlantı Başarılı',
          message: res.message || 'MySQL veritabanına başarıyla bağlanıldı ve tüm tablolar aktif.',
          serverVersion: res.serverVersion,
          database: res.database,
          tablesCount: res.tablesCount,
          pingMs: res.pingMs,
          time: timeStr,
        });
      } else {
        setDbTestResult({
          success: false,
          title: 'Veritabanı Bağlantı Hatası',
          message: res.error || res.message || 'Veritabanına bağlanılamadı.',
          troubleshooting: res.troubleshooting,
          pingMs: res.pingMs,
          time: timeStr,
        });
      }
    } catch (err: any) {
      setDbTestResult({
        success: false,
        title: 'Veritabanı Bağlantı Hatası',
        message: err.message || 'Sunucu ile iletişim kurulamadı.',
        time: new Date().toLocaleTimeString('tr-TR'),
      });
    } finally {
      setIsTestingDb(false);
    }
  };

  const handleDownloadSql = async () => {
    try {
      setDownloadingSql(true);
      await downloadSqlDump();
    } catch (err: any) {
      console.error('SQL export error:', err);
    } finally {
      setDownloadingSql(false);
    }
  };

  const handleRestoreFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!confirm('Seçilen yedek dosyasını geri yüklemek istediğinize emin misiniz? Bu işlem sunucudaki ve tarayıcıdaki tüm verileri güncelleyecektir.')) {
      e.target.value = '';
      return;
    }

    setRestoringBackup(true);
    setRestoreMessage(null);

    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const res = await restoreFullDatabaseData(json);
      if (res.success) {
        setRestoreMessage({ type: 'success', text: 'Yedek başarıyla geri yüklendi! Sayfa yenileniyor...' });
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setRestoreMessage({ type: 'error', text: res.message || 'Yedek yüklenirken bir hata oluştu.' });
      }
    } catch (err: any) {
      setRestoreMessage({ type: 'error', text: 'Geçersiz JSON yedek dosyası: ' + err.message });
    } finally {
      setRestoringBackup(false);
      e.target.value = '';
    }
  };

  // Check if a user is the primary/master system admin
  const isMasterAdminUser = (user: AppUser | null | undefined): boolean => {
    if (!user) return false;
    const masterUsername = (companyInfo.adminUsername || 'admin').trim().toLowerCase();
    const targetUsername = (user.username || '').trim().toLowerCase();
    return (
      user.id === 'usr_admin' ||
      targetUsername === masterUsername ||
      targetUsername === 'admin'
    );
  };

  const loggedInUser = getCurrentUser();
  const isCurrentLoggedInMasterAdmin = isMasterAdminUser(loggedInUser);

  // Güvenlik: mevcut parolalar tek yönlü hash tutulur; yalnızca yeni girilen parola göz simgesiyle gösterilebilir.

  // Keep form in sync with companyInfo ONLY when modal opens (not on background polling ticks)
  useEffect(() => {
    if (isOpen) {
      if (initialTab === 'security' && !isCurrentLoggedInMasterAdmin) {
        setActiveTab('company');
      } else {
        setActiveTab(initialTab);
      }
      setName(companyInfo.name || '');
      setPhone(companyInfo.phone || '');
      setEmail(companyInfo.email || '');
      setAddress(companyInfo.address || '');
      setLogoUrl(companyInfo.logoUrl || '');
      setLogoError(null);
      setTaxOffice(companyInfo.taxOffice || '');
      setTaxNumber(companyInfo.taxNumber || '');
      setTerms(companyInfo.terms || '');
      setDeviceAreaAssignments(companyInfo.deviceAreaAssignments || {});
      setAdminUsername(companyInfo.adminUsername || 'admin');
      setAdminPassword('');
      setSecurityQuestion(companyInfo.securityQuestion || 'İlk evcil hayvanınızın adı nedir?');
      setSecurityAnswer('');

      setUsersList(getStoredUsers());
      setEditingUserId(null);
      setUserFormError(null);
      setUserSuccessMessage(null);

      const currentSms = getLocalSmsSettings();
      setSmsSettings(currentSms);
      setTestSmsStatus(null);
      setSmsBalance(null);
      setSmsSavedNotice(false);

      setDeviceTypesList(deviceTypes && deviceTypes.length > 0 ? deviceTypes : getStoredDeviceTypes());
      setDeviceBrandsList(deviceBrands && deviceBrands.length > 0 ? deviceBrands : getStoredDeviceBrands());
      setNewTypeName('');
      setNewBrandName('');
      setBrandSearchQuery('');
      setDeviceSuccessMsg(null);
      setDeviceErrorMsg(null);
    }
  }, [isOpen]);

  // Device Management Handlers
  const handleAddDeviceType = () => {
    const trimmed = newTypeName.trim();
    if (!trimmed) {
      setDeviceErrorMsg('Lütfen geçerli bir cihaz türü adı giriniz.');
      setTimeout(() => setDeviceErrorMsg(null), 3000);
      return;
    }
    if (deviceTypesList.some(t => t.toLowerCase() === trimmed.toLowerCase())) {
      setDeviceErrorMsg('Bu cihaz türü zaten listede mevcut.');
      setTimeout(() => setDeviceErrorMsg(null), 3000);
      return;
    }
    const updated = [...deviceTypesList, trimmed];
    setDeviceTypesList(updated);
    saveDeviceTypes(updated);
    if (onSaveDeviceTypes) onSaveDeviceTypes(updated);
    setNewTypeName('');
    setDeviceSuccessMsg(`"${trimmed}" cihaz türü başarıyla eklendi.`);
    setTimeout(() => setDeviceSuccessMsg(null), 3000);
  };

  const handleDeleteDeviceType = (typeName: string) => {
    if (deviceTypesList.length <= 1) {
      setDeviceErrorMsg('En az bir cihaz türü kayıtlı kalmalıdır.');
      setTimeout(() => setDeviceErrorMsg(null), 3000);
      return;
    }
    if (window.confirm(`"${typeName}" cihaz türünü silmek istediğinize emin misiniz?`)) {
      const updated = deviceTypesList.filter(t => t !== typeName);
      setDeviceTypesList(updated);
      saveDeviceTypes(updated);
      if (onSaveDeviceTypes) onSaveDeviceTypes(updated);
      setDeviceSuccessMsg(`"${typeName}" cihaz türü silindi.`);
      setTimeout(() => setDeviceSuccessMsg(null), 3000);
    }
  };

  const handleResetDeviceTypes = () => {
    if (window.confirm('Cihaz türlerini varsayılan listeye sıfırlamak istiyor musunuz?')) {
      setDeviceTypesList(defaultDeviceTypes);
      saveDeviceTypes(defaultDeviceTypes);
      if (onSaveDeviceTypes) onSaveDeviceTypes(defaultDeviceTypes);
      setDeviceSuccessMsg('Cihaz türleri varsayılana sıfırlandı.');
      setTimeout(() => setDeviceSuccessMsg(null), 3000);
    }
  };

  const handleAddDeviceBrand = () => {
    const trimmed = newBrandName.trim();
    if (!trimmed) {
      setDeviceErrorMsg('Lütfen geçerli bir marka adı giriniz.');
      setTimeout(() => setDeviceErrorMsg(null), 3000);
      return;
    }
    if (deviceBrandsList.some(b => b.toLowerCase() === trimmed.toLowerCase())) {
      setDeviceErrorMsg('Bu marka zaten listede kayıtlı.');
      setTimeout(() => setDeviceErrorMsg(null), 3000);
      return;
    }
    const updated = [...deviceBrandsList, trimmed].sort((a, b) => a.localeCompare(b, 'tr'));
    setDeviceBrandsList(updated);
    saveDeviceBrands(updated);
    if (onSaveDeviceBrands) onSaveDeviceBrands(updated);
    setNewBrandName('');
    setDeviceSuccessMsg(`"${trimmed}" markası başarıyla kaydedildi.`);
    setTimeout(() => setDeviceSuccessMsg(null), 3000);
  };

  const handleDeleteDeviceBrand = (brandName: string) => {
    if (deviceBrandsList.length <= 1) {
      setDeviceErrorMsg('En az bir marka kayıtlı kalmalıdır.');
      setTimeout(() => setDeviceErrorMsg(null), 3000);
      return;
    }
    if (window.confirm(`"${brandName}" markasını silmek istediğinize emin misiniz?`)) {
      const updated = deviceBrandsList.filter(b => b.toLowerCase() !== brandName.toLowerCase());
      setDeviceBrandsList(updated);
      saveDeviceBrands(updated);
      if (onSaveDeviceBrands) onSaveDeviceBrands(updated);
      setDeviceSuccessMsg(`"${brandName}" markası silindi.`);
      setTimeout(() => setDeviceSuccessMsg(null), 3000);
    }
  };

  const handleResetDeviceBrands = () => {
    if (window.confirm('Markaları varsayılan listeye sıfırlamak istiyor musunuz?')) {
      setDeviceBrandsList(defaultDeviceBrands);
      saveDeviceBrands(defaultDeviceBrands);
      if (onSaveDeviceBrands) onSaveDeviceBrands(defaultDeviceBrands);
      setDeviceSuccessMsg('Markalar varsayılana sıfırlandı.');
      setTimeout(() => setDeviceSuccessMsg(null), 3000);
    }
  };

  const handleOpenAddUser = () => {
    setEditingUserId('new');
    setUserFormName('');
    setUserFormUsername('');
    setUserFormPassword('');
    setUserFormPhone('');
    setUserFormRole('technician');
    setUserFormIsActive(true);
    setUserFormError(null);
  };

  const handleOpenEditUser = (user: AppUser) => {
    if (isMasterAdminUser(user) && !isCurrentLoggedInMasterAdmin) {
      alert('Ana yönetici (admin) bilgileri yalnızca ana yönetici tarafından görüntülenebilir ve düzenlenebilir.');
      return;
    }
    setEditingUserId(user.id);
    setUserFormName(user.name);
    setUserFormUsername(user.username);
    setUserFormPassword('');
    setUserFormPhone(user.phone || '');
    setUserFormRole(user.role);
    setUserFormIsActive(user.isActive !== false);
    setUserFormError(null);
  };

  const handleSaveUserForm = (e: React.FormEvent) => {
    e.preventDefault();
    setUserFormError(null);

    const cleanUsername = userFormUsername.trim().toLowerCase();
    const cleanPassword = userFormPassword.trim();
    const cleanName = userFormName.trim();

    if (!cleanName) {
      setUserFormError('Lütfen ad ve soyad giriniz.');
      return;
    }
    if (!cleanUsername) {
      setUserFormError('Lütfen kullanıcı adı giriniz.');
      return;
    }
    // Check if the user being edited is the Master Admin
    const targetUser = editingUserId !== 'new' ? usersList.find(u => u.id === editingUserId) : null;
    const isTargetMaster =
      isMasterAdminUser(targetUser) ||
      cleanUsername === (companyInfo.adminUsername || 'admin').toLowerCase() ||
      cleanUsername === 'admin' ||
      editingUserId === 'usr_admin';

    if (isTargetMaster) {
      if (!cleanName) {
        setUserFormError('Lütfen ad ve soyad giriniz.');
        return;
      }
      const updatedUsers = usersList.map(u => {
        if (u.id === editingUserId || isMasterAdminUser(u)) {
          return {
            ...u,
            name: cleanName,
            phone: userFormPhone.trim(),
            role: 'admin' as const,
            isActive: true,
          };
        }
        return u;
      });
      setUsersList(updatedUsers);
      saveUsers(updatedUsers);
      setUserSuccessMessage(`Ana yönetici bilgileri (${cleanName}) güncellendi.`);
      const currentLoggedInUser = getCurrentUser();
      if (currentLoggedInUser && (currentLoggedInUser.id === editingUserId || isMasterAdminUser(currentLoggedInUser))) {
        const updatedSelf = updatedUsers.find(u => u.id === editingUserId || isMasterAdminUser(u));
        if (updatedSelf) {
          setCurrentUser(updatedSelf);
        }
      }
      setEditingUserId(null);
      setTimeout(() => setUserSuccessMessage(null), 4000);
      return;
    }

    if (editingUserId === 'new' && (!cleanPassword || cleanPassword.length < 3)) {
      setUserFormError('Şifre en az 3 karakter olmalıdır.');
      return;
    }
    if (cleanPassword && cleanPassword.length < 3) {
      setUserFormError('Yeni şifre en az 3 karakter olmalıdır.');
      return;
    }

    // Check duplicate username
    const exists = usersList.some(
      u => u.username.toLowerCase() === cleanUsername && u.id !== editingUserId
    );
    if (exists) {
      setUserFormError('Bu kullanıcı adı zaten başka bir kullanıcı tarafından kullanılıyor.');
      return;
    }

    let updatedUsers: AppUser[];
    if (editingUserId === 'new') {
      const newUser: AppUser = {
        id: 'usr_' + Date.now(),
        name: cleanName,
        username: cleanUsername,
        password: cleanPassword,
        phone: userFormPhone.trim(),
        role: userFormRole,
        isActive: userFormIsActive,
        createdAt: new Date().toISOString(),
      };
      updatedUsers = [...usersList, newUser];
      setUserSuccessMessage(`"${cleanName}" kullanıcısı başarıyla eklendi.`);
    } else {
      updatedUsers = usersList.map(u => {
        if (u.id === editingUserId) {
          return {
            ...u,
            name: cleanName,
            username: cleanUsername,
            password: cleanPassword,
            phone: userFormPhone.trim(),
            role: userFormRole,
            isActive: userFormIsActive,
          };
        }
        return u;
      });
      setUserSuccessMessage(`"${cleanName}" kullanıcı bilgileri güncellendi.`);
    }

    setUsersList(updatedUsers);
    saveUsers(updatedUsers);

    // If current logged-in user edited themselves, keep session updated
    const currentLoggedInUser = getCurrentUser();
    if (currentLoggedInUser && (currentLoggedInUser.id === editingUserId || currentLoggedInUser.username.toLowerCase() === cleanUsername)) {
      const updatedSelf = updatedUsers.find(u => u.id === editingUserId || u.username.toLowerCase() === cleanUsername);
      if (updatedSelf) {
        setCurrentUser(updatedSelf);
      }
    }

    setEditingUserId(null);

    setTimeout(() => {
      setUserSuccessMessage(null);
    }, 4000);
  };

  const handleDeleteUser = (user: AppUser) => {
    if (isMasterAdminUser(user)) {
      alert('Ana yönetici (admin) hesabı silinemez.');
      return;
    }
    if (window.confirm(`"${user.name}" (${user.username}) kullanıcısını silmek istediğinize emin misiniz?`)) {
      const updated = usersList.filter(u => u.id !== user.id);
      setUsersList(updated);
      saveUsers(updated);
      setUserSuccessMessage(`"${user.name}" kullanıcısı silindi.`);
      setTimeout(() => setUserSuccessMessage(null), 3000);
    }
  };

  const handleToggleUserStatus = (user: AppUser) => {
    if (isMasterAdminUser(user)) {
      alert('Ana yönetici (admin) hesabı sistem güvenliği ve erişim sürekliliği için pasife alınamaz, her zaman aktif kalmalıdır.');
      return;
    }
    const updated = usersList.map(u => {
      if (u.id === user.id) {
        return { ...u, isActive: !u.isActive };
      }
      return u;
    });
    setUsersList(updated);
    saveUsers(updated);
  };

  // Real-time update helper for SMS settings (prevents loss on keystrokes)
  const handleUpdateSmsSettings = (updates: Partial<SmsSettings>) => {
    setSmsSettings(prev => {
      const next = { ...prev, ...updates };
      saveLocalSmsSettings(next);
      return next;
    });
  };

  const handleSaveSmsOnly = async () => {
    setSavingSms(true);
    try {
      saveLocalSmsSettings(smsSettings);
      await saveServerSmsSettings(smsSettings);
      setSmsSavedNotice(true);
      setTimeout(() => setSmsSavedNotice(false), 2500);
    } catch (e) {
      console.error('Error saving SMS settings:', e);
    } finally {
      setSavingSms(false);
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setLogoError('Lütfen geçerli bir resim dosyası seçin (PNG, JPG, WEBP, SVG).');
      return;
    }

    setLogoError(null);
    const reader = new FileReader();
    reader.onload = (event) => {
      const rawDataUrl = event.target?.result as string;
      if (!rawDataUrl) return;

      const img = new Image();
      img.onload = () => {
        const maxW = 500;
        const maxH = 260;
        let width = img.width;
        let height = img.height;

        if (width > maxW || height > maxH) {
          if (width / maxW > height / maxH) {
            height = Math.round((height * maxW) / width);
            width = maxW;
          } else {
            width = Math.round((width * maxH) / height);
            height = maxH;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          const optimizedUrl = canvas.toDataURL(file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png', 0.92);
          setLogoUrl(optimizedUrl);
        } else {
          setLogoUrl(rawDataUrl);
        }
      };
      img.onerror = () => {
        setLogoUrl(rawDataUrl);
      };
      img.src = rawDataUrl;
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoUrl('');
    setLogoError(null);
  };

  const handleCheckBalance = async () => {
    if (!smsSettings.apiKey || !smsSettings.apiHash) {
      setSmsBalance('Lütfen önce API Key ve API Hash giriniz.');
      return;
    }
    setCheckingBalance(true);
    setSmsBalance(null);
    try {
      const res = await checkSmsBalance(smsSettings.apiKey, smsSettings.apiHash);
      if (res.success && res.balance) {
        const smsCount = res.balance.sms || res.balance.amount || JSON.stringify(res.balance);
        setSmsBalance(`Kalan Kredi: ${smsCount} SMS`);
      } else {
        setSmsBalance(res.message || 'Bakiye sorgulanamadı.');
      }
    } catch (e: any) {
      setSmsBalance('Hata: ' + e.message);
    } finally {
      setCheckingBalance(false);
    }
  };

  const handleSendTestSms = async () => {
    if (!smsSettings.apiKey || !smsSettings.apiHash || !smsSettings.sender) {
      setTestSmsStatus({
        type: 'error',
        text: 'Lütfen API Key, API Hash ve Başlık alanlarını eksiksiz doldurunuz.',
      });
      return;
    }
    if (!testPhone.trim()) {
      setTestSmsStatus({
        type: 'error',
        text: 'Lütfen test SMS gönderilecek bir telefon numarası giriniz.',
      });
      return;
    }

    setSendingTestSms(true);
    setTestSmsStatus(null);
    try {
      const res = await sendSmsMessage({
        phone: testPhone.trim(),
        text: `[${smsSettings.sender}] Teknik Servis Sistemi test SMS mesajıdır. İletimerkezi API entegrasyonu başarıyla aktif edilmiştir.`,
        customApiKey: smsSettings.apiKey,
        customApiHash: smsSettings.apiHash,
        customSender: smsSettings.sender,
      });

      if (res.success) {
        setTestSmsStatus({
          type: 'success',
          text: `Test SMS başarıyla iletildi! ${res.orderId ? `(Sipariş No: ${res.orderId})` : ''}`,
        });
      } else {
        setTestSmsStatus({
          type: 'error',
          text: res.message || 'SMS gönderimi başarısız oldu.',
        });
      }
    } catch (e: any) {
      setTestSmsStatus({
        type: 'error',
        text: 'Hata: ' + e.message,
      });
    } finally {
      setSendingTestSms(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isCurrentLoggedInMasterAdmin) {
      const isPasswordChanged =
        adminPassword.trim().length > 0 ||
        adminUsername.trim() !== (companyInfo.adminUsername || 'admin');

      const newPasswordVersion = isPasswordChanged
        ? 'pv_' + Date.now()
        : (companyInfo.passwordVersion || '1');

      const cleanAdminPass = adminPassword.trim();
      const cleanSecurityAnswer = securityAnswer.trim();

      // Save company info (clean, plain fields)
      onSave({
        ...companyInfo,
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        address: address.trim(),
        logoUrl: logoUrl.trim() ? logoUrl.trim() : '',
        taxOffice: taxOffice.trim() || undefined,
        taxNumber: taxNumber.trim() || undefined,
        terms: terms.trim(),
        deviceAreaAssignments,
        adminUsername: adminUsername.trim() || 'admin',
        ...(cleanAdminPass ? { adminPassword: cleanAdminPass } : {}),
        securityQuestion: securityQuestion.trim(),
        ...(cleanSecurityAnswer ? { securityAnswer: cleanSecurityAnswer } : {}),
        passwordVersion: newPasswordVersion,
      });

      // Also sync master admin in usersList if changed (user password is encrypted)
      const updatedUsers = usersList.map(u => {
        if (isMasterAdminUser(u)) {
          return {
            ...u,
            username: adminUsername.trim() || 'admin',
            ...(cleanAdminPass ? { password: cleanAdminPass } : {}),
          };
        }
        return u;
      });
      setUsersList(updatedUsers);
      saveUsers(updatedUsers);

      // Keep this browser's session valid with the new password version
      setCurrentAuthVersion(newPasswordVersion);
      const currentAdmin = updatedUsers.find(isMasterAdminUser) || updatedUsers[0];
      if (currentAdmin) {
        setCurrentUser(currentAdmin);
      }
    } else {
      // Secondary admin saves company details without overwriting master admin credentials
      onSave({
        ...companyInfo,
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        address: address.trim(),
        logoUrl: logoUrl.trim() ? logoUrl.trim() : '',
        taxOffice: taxOffice.trim() || undefined,
        taxNumber: taxNumber.trim() || undefined,
        terms: terms.trim(),
        deviceAreaAssignments,
      });
    }

    // Save SMS settings
    await saveServerSmsSettings(smsSettings);

    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/70 backdrop-blur-sm">
      <div className="min-h-full flex items-center justify-center p-3 sm:p-4 text-center">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl lg:max-w-4xl border border-slate-200 overflow-hidden my-auto text-left transform transition-all flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between border-b border-slate-800 shrink-0 gap-3">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white leading-tight">Sistem &amp; Firma Ayarları</h2>
              <p className="text-xs text-slate-400">Firma bilgileri, İletimerkezi SMS entegrasyonu ve kullanıcı/teknisyen yetkileri</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleTestDatabaseConnection}
              disabled={isTestingDb}
              title="Sunucu ile MySQL veritabanı arasındaki canlı bağlantıyı test eder"
              className="px-3 py-1.5 bg-blue-600/90 hover:bg-blue-600 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all shadow-xs border border-blue-500/30 cursor-pointer disabled:opacity-60"
            >
              <Database className="w-3.5 h-3.5 text-blue-200" />
              {isTestingDb ? (
                <RefreshCw className="w-3.5 h-3.5 animate-spin text-white" />
              ) : null}
              <span>{isTestingDb ? 'Test Ediliyor...' : 'Veritabanı Test Et'}</span>
            </button>

            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-200 bg-slate-50 px-6 pt-2 shrink-0 gap-2 overflow-x-auto">
          <button
            type="button"
            onClick={() => setActiveTab('company')}
            className={`px-4 py-2 text-xs font-bold rounded-t-xl transition-all border-t border-x cursor-pointer flex items-center gap-1.5 whitespace-nowrap ${
              activeTab === 'company'
                ? 'bg-white text-blue-600 border-slate-200 border-b-white -mb-px shadow-xs'
                : 'bg-transparent text-slate-600 border-transparent hover:text-slate-900'
            }`}
          >
            <Building2 className="w-3.5 h-3.5" />
            <span>Firma &amp; Fatura</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('sms')}
            className={`px-4 py-2 text-xs font-bold rounded-t-xl transition-all border-t border-x cursor-pointer flex items-center gap-1.5 whitespace-nowrap ${
              activeTab === 'sms'
                ? 'bg-white text-blue-600 border-slate-200 border-b-white -mb-px shadow-xs'
                : 'bg-transparent text-slate-600 border-transparent hover:text-slate-900'
            }`}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            <span>SMS Entegrasyonu</span>
            {smsSettings.enabled && (
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
            )}
          </button>

          {isCurrentLoggedInMasterAdmin && (
            <button
              type="button"
              onClick={() => setActiveTab('security')}
              className={`px-4 py-2 text-xs font-bold rounded-t-xl transition-all border-t border-x cursor-pointer flex items-center gap-1.5 whitespace-nowrap ${
                activeTab === 'security'
                  ? 'bg-white text-blue-600 border-slate-200 border-b-white -mb-px shadow-xs'
                  : 'bg-transparent text-slate-600 border-transparent hover:text-slate-900'
              }`}
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Giriş &amp; Güvenlik</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => setActiveTab('devices')}
            className={`px-4 py-2 text-xs font-bold rounded-t-xl transition-all border-t border-x cursor-pointer flex items-center gap-1.5 whitespace-nowrap ${
              activeTab === 'devices'
                ? 'bg-white text-blue-600 border-slate-200 border-b-white -mb-px shadow-xs'
                : 'bg-transparent text-slate-600 border-transparent hover:text-slate-900'
            }`}
          >
            <Smartphone className="w-3.5 h-3.5" />
            <span>Cihazlar</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 text-xs font-bold rounded-t-xl transition-all border-t border-x cursor-pointer flex items-center gap-1.5 whitespace-nowrap ${
              activeTab === 'users'
                ? 'bg-white text-blue-600 border-slate-200 border-b-white -mb-px shadow-xs'
                : 'bg-transparent text-slate-600 border-transparent hover:text-slate-900'
            }`}
          >
            <Users className="w-3.5 h-3.5" />
            <span>Kullanıcılar &amp; Teknisyenler</span>
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-blue-100 text-blue-700 font-bold">
              {usersList.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('database')}
            className={`px-4 py-2 text-xs font-bold rounded-t-xl transition-all border-t border-x cursor-pointer flex items-center gap-1.5 whitespace-nowrap ${
              activeTab === 'database'
                ? 'bg-white text-blue-600 border-slate-200 border-b-white -mb-px shadow-xs'
                : 'bg-transparent text-slate-600 border-transparent hover:text-slate-900'
            }`}
          >
            <Database className="w-3.5 h-3.5 text-blue-600" />
            <span>Veritabanı &amp; Tablolar</span>
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-emerald-100 text-emerald-800 font-bold">
              17 Tablo
            </span>
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1 text-slate-700">
          {/* Canlı Veritabanı Test Sonuç Bildirimi (Kullanıcı İsteği) */}
          {dbTestResult && (
            <div
              className={`p-4 rounded-xl border transition-all animate-in fade-in flex items-start justify-between gap-3 shadow-sm ${
                dbTestResult.success
                  ? 'bg-emerald-50/95 border-emerald-300 text-emerald-950'
                  : 'bg-rose-50/95 border-rose-300 text-rose-950'
              }`}
            >
              <div className="flex items-start space-x-3">
                <div
                  className={`p-2 rounded-lg shrink-0 ${
                    dbTestResult.success ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                  }`}
                >
                  {dbTestResult.success ? (
                    <CheckCircle2 className="w-5 h-5" />
                  ) : (
                    <AlertCircle className="w-5 h-5" />
                  )}
                </div>
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-extrabold text-sm">
                      {dbTestResult.title}
                    </span>
                    {dbTestResult.time && (
                      <span className="text-[10px] text-slate-500 font-mono">({dbTestResult.time})</span>
                    )}
                    {dbTestResult.pingMs !== undefined && (
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold font-mono ${
                          dbTestResult.success ? 'bg-emerald-200/80 text-emerald-900' : 'bg-rose-200/80 text-rose-900'
                        }`}
                      >
                        {dbTestResult.pingMs} ms
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-semibold leading-relaxed">{dbTestResult.message}</p>
                  {dbTestResult.success && dbTestResult.database && (
                    <div className="text-[11px] text-emerald-800 font-mono mt-1">
                      Veritabanı: <strong>{dbTestResult.database}</strong> | Sunucu Sürümü: <strong>{dbTestResult.serverVersion || 'MySQL 8.0+'}</strong> | Tablo Sayısı: <strong>{dbTestResult.tablesCount ?? 17}</strong>
                    </div>
                  )}
                  {!dbTestResult.success && dbTestResult.troubleshooting && (
                    <div className="bg-white/90 border border-rose-200 rounded-lg p-2.5 mt-2 text-[11px] text-slate-700">
                      <strong className="text-rose-900 flex items-center gap-1 mb-0.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                        Teşhis &amp; Önerilen Çözüm:
                      </strong>
                      {dbTestResult.troubleshooting}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {!dbTestResult.success && (
                  <button
                    type="button"
                    onClick={handleTestDatabaseConnection}
                    disabled={isTestingDb}
                    className="px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold transition-colors cursor-pointer flex items-center gap-1"
                  >
                    <RefreshCw className={`w-3 h-3 ${isTestingDb ? 'animate-spin' : ''}`} />
                    <span>Tekrar Dene</span>
                  </button>
                )}
                {activeTab !== 'database' && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('database')}
                    className="px-2.5 py-1 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                  >
                    Tablolara Git
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setDbTestResult(null)}
                  className="text-slate-400 hover:text-slate-700 p-1 rounded-md transition-colors cursor-pointer"
                  title="Bildirimi Kapat"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* TAB 1: COMPANY INFO */}
          {activeTab === 'company' && (
            <div className="space-y-4">
              {/* Online Cihaz Takip Portalı Doğrudan Link Banner */}
              <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border border-blue-200/90 rounded-xl p-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-xs">
                <div className="flex items-start space-x-3">
                  <div className="w-9 h-9 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-xs mt-0.5">
                    <Smartphone className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <span>Müşteri Online Cihaz Durum Takip Linki</span>
                    </h4>
                    <p className="text-[11px] text-slate-600 mt-0.5">
                      Müşterileriniz bu linke doğrudan girerek Form No ve Telefonları ile durum sorgulayabilir.
                    </p>
                    <div className="mt-1.5 flex items-center gap-1.5 font-mono text-[11px] text-blue-700 font-semibold bg-white/80 px-2 py-1 rounded-md border border-blue-200 inline-flex max-w-full overflow-x-auto">
                      <span>{typeof window !== 'undefined' ? `${window.location.origin}/takip` : '/takip'}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 self-end sm:self-center shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      const url = typeof window !== 'undefined' ? `${window.location.origin}/takip` : '';
                      navigator.clipboard.writeText(url);
                      setCopiedPortalSettingsLink(true);
                      setTimeout(() => setCopiedPortalSettingsLink(false), 2000);
                    }}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer"
                  >
                    {copiedPortalSettingsLink ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-300" />
                        <span>Kopyalandı</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        <span>Linki Kopyala</span>
                      </>
                    )}
                  </button>
                  <a
                    href="/takip"
                    target="_blank"
                    rel="noreferrer"
                    className="px-2.5 py-1.5 bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors"
                  >
                    <span>Aç</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>

              {/* Logo Yükleme Alanı */}
              <div className="bg-slate-50 border border-slate-200/90 rounded-xl p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <ImageIcon className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-bold text-slate-800">Firma Logosu</span>
                  </div>
                  {logoUrl && (
                    <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Check className="w-3 h-3" /> Logo Yüklü
                    </span>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-4 pt-1">
                  <div className="w-full sm:w-44 h-24 bg-white border border-slate-200 rounded-xl flex items-center justify-center p-2 shadow-xs shrink-0 overflow-hidden relative">
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt="Firma Logosu"
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <div className="text-center text-slate-400 flex flex-col items-center">
                        <ImageIcon className="w-7 h-7 stroke-1 mb-1 text-slate-300" />
                        <span className="text-[10px] font-medium">Logo Yok</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 flex-1 w-full text-center sm:text-left">
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Yüklediğiniz logo <strong className="text-slate-700">Müşteri Kabul Formu</strong> ve <strong className="text-slate-700">Fatura / Makbuz</strong> çıktılarında yer alacaktır.
                    </p>
                    
                    <div className="flex flex-wrap items-center gap-2 justify-center sm:justify-start">
                      <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold cursor-pointer transition-colors shadow-xs">
                        <Upload className="w-3.5 h-3.5" />
                        <span>{logoUrl ? 'Logoyu Değiştir' : 'Logo Yükle'}</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                        />
                      </label>

                      {logoUrl && (
                        <button
                          type="button"
                          onClick={handleRemoveLogo}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>Logoyu Kaldır</span>
                        </button>
                      )}
                    </div>

                    {logoError && (
                      <p className="text-xs text-rose-500 font-medium">{logoError}</p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Firma Ünvanı / İşyeri Adı <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Örn: Hızlı Teknik Bilişim & Elektronik"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Telefon Numarası
                  </label>
                  <div className="relative">
                    <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      placeholder="05XX XXX XX XX"
                      className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    E-Posta Adresi
                  </label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="info@teknikservis.com"
                      className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Vergi Dairesi
                  </label>
                  <input
                    type="text"
                    value={taxOffice}
                    onChange={e => setTaxOffice(e.target.value)}
                    placeholder="Örn: Beyoğlu V.D."
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Vergi / T.C. Kimlik No
                  </label>
                  <input
                    type="text"
                    value={taxNumber}
                    onChange={e => setTaxNumber(e.target.value)}
                    placeholder="Örn: 1234567890"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  İşyeri / Servis Adresi
                </label>
                <div className="relative">
                  <MapPin className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                  <textarea
                    rows={2}
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    placeholder="Tam adres bilgisi..."
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Servis Kabul ve Garanti Şartları (Fiş Altı Metni)
                </label>
                <div className="relative">
                  <FileText className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                  <textarea
                    rows={4}
                    value={terms}
                    onChange={e => setTerms(e.target.value)}
                    placeholder="Her satıra bir madde gelecek şekilde servis şartlarınızı girin..."
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-mono"
                  />
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: SMS INTEGRATION (ILETIMERKEZI) */}
          {activeTab === 'sms' && (
            <div className="space-y-5">
              {/* Top Banner & Enable Switch + Quick Save */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0">
                    <MessageSquare className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">İletimerkezi SMS Entegrasyonu</h4>
                    <p className="text-xs text-slate-600">
                      Müşterilerinize servis kabul, cihaz hazır ve teslimat durumlarında otomatik SMS gönderin.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <label className="flex items-center space-x-2 cursor-pointer bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-xs">
                    <input
                      type="checkbox"
                      checked={smsSettings.enabled}
                      onChange={e => handleUpdateSmsSettings({ enabled: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-xs font-bold text-slate-800">
                      {smsSettings.enabled ? 'SMS Aktif' : 'SMS Kapalı'}
                    </span>
                  </label>

                  <button
                    type="button"
                    onClick={handleSaveSmsOnly}
                    disabled={savingSms}
                    className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-xs flex items-center gap-1.5 transition-all cursor-pointer"
                  >
                    <Check className="w-3.5 h-3.5" />
                    <span>{savingSms ? 'Kaydediliyor...' : 'SMS Ayarlarını Kaydet'}</span>
                  </button>
                </div>
              </div>

              {smsSavedNotice && (
                <div className="p-3 bg-emerald-50 border border-emerald-300 text-emerald-800 rounded-xl text-xs font-bold flex items-center gap-2 animate-in fade-in duration-200">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>SMS API bilgileri ve şablonları başarıyla kaydedildi!</span>
                </div>
              )}

              {/* API Credentials */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                    <KeyRound className="w-4 h-4 text-blue-600" />
                    <span>İletimerkezi API Giriş Bilgileri</span>
                  </h4>
                  <div className="flex items-center space-x-2">
                    {smsBalance && (
                      <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                        {smsBalance}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={handleCheckBalance}
                      disabled={checkingBalance}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1 cursor-pointer bg-blue-50 px-2.5 py-1 rounded-lg border border-blue-200"
                    >
                      <RefreshCw className={`w-3 h-3 ${checkingBalance ? 'animate-spin' : ''}`} />
                      <span>{checkingBalance ? 'Sorgulanıyor...' : 'Bakiye Sorgula'}</span>
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">
                      API Anahtarı (Key) <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={smsSettings.apiKey}
                      onChange={e => handleUpdateSmsSettings({ apiKey: e.target.value })}
                      placeholder="Örn: 2c19e53b8f..."
                      className="w-full px-3 py-1.5 text-xs font-mono border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">
                      API Şifresi (Hash / Secret) <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={smsSettings.apiHash}
                      onChange={e => handleUpdateSmsSettings({ apiHash: e.target.value })}
                      placeholder="••••••••••••••••"
                      className="w-full px-3 py-1.5 text-xs font-mono border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">
                      Gönderici Başlığı (Sender / Header) <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={smsSettings.sender}
                      onChange={e => handleUpdateSmsSettings({ sender: e.target.value.toUpperCase() })}
                      placeholder="Örn: TEKNIKSRV"
                      className="w-full px-3 py-1.5 text-xs font-bold font-mono uppercase border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">
                      İYS Durumu (İYS Listesi)
                    </label>
                    <select
                      value={smsSettings.iysList}
                      onChange={e => handleUpdateSmsSettings({ iysList: e.target.value as any })}
                      className="w-full px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                    >
                      <option value="BIREYSEL">BIREYSEL</option>
                      <option value="TACIR">TACIR</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">
                      İYS Filtresi
                    </label>
                    <select
                      value={smsSettings.iys}
                      onChange={e => handleUpdateSmsSettings({ iys: e.target.value as any })}
                      className="w-full px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                    >
                      <option value="1">1 (İYS Onaylılara Gönder)</option>
                      <option value="0">0 (İYS Kontrolsüz Doğrudan Gönder)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Automatic Triggers */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2.5">
                <h4 className="text-xs font-bold text-slate-800">
                  Otomatik SMS Bildirim Kuralları
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 pt-1">
                  <label className="flex items-center space-x-2 bg-white p-2.5 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={smsSettings.autoSendOnCreate}
                      onChange={e => handleUpdateSmsSettings({ autoSendOnCreate: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-xs font-semibold text-slate-700">
                      Kayıt Alındığında
                    </span>
                  </label>

                  <label className="flex items-center space-x-2 bg-white p-2.5 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={smsSettings.autoSendOnApprovalWaiting || false}
                      onChange={e => handleUpdateSmsSettings({ autoSendOnApprovalWaiting: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-xs font-semibold text-slate-700">
                      Onay Bekliyor Durumunda
                    </span>
                  </label>

                  <label className="flex items-center space-x-2 bg-white p-2.5 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={smsSettings.autoSendOnReady}
                      onChange={e => handleUpdateSmsSettings({ autoSendOnReady: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-xs font-semibold text-slate-700">
                      Teslimata Hazır Olduğunda
                    </span>
                  </label>

                  <label className="flex items-center space-x-2 bg-white p-2.5 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={smsSettings.autoSendOnDelivered}
                      onChange={e => handleUpdateSmsSettings({ autoSendOnDelivered: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-xs font-semibold text-slate-700">
                      Teslim Edildiğinde
                    </span>
                  </label>
                </div>
              </div>

              {/* SMS Templates Editor */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 space-y-4">
                <div className="border-b border-slate-100 pb-3">
                  <h4 className="text-sm font-bold text-slate-800">
                    SMS Şablonları &amp; Değişkenler
                  </h4>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Metin kutularına değişken eklemek için aşağıdaki butonlara tıklayabilirsiniz.
                  </p>
                </div>

                {/* Reusable Template Field Helper */}
                {(() => {
                  const placeholders = [
                    { tag: '{MUSTERI_ADI}', label: 'Müşteri Adı', desc: 'Müşterinin adı soyadı' },
                    { tag: '{SERVIS_NO}', label: 'Servis No', desc: 'Fiş/Kayıt numarası' },
                    { tag: '{TAKIP_LINKI}', label: '🔗 Takip Linki', desc: 'Müşteri form no ve telefon bilgisiyle doğrudan açılan takip bağlantısı' },
                    { tag: '{CIHAZ}', label: 'Cihaz', desc: 'Marka & model bilgisi' },
                    { tag: '{TUTAR}', label: 'Tutar', desc: 'Onarım ücreti / tutar' },
                    { tag: '{FIRMA_ADI}', label: 'Firma Adı', desc: 'İşyerinizin ünvanı' },
                  ];

                  const insertTag = (
                    fieldKey: keyof typeof smsSettings.templates,
                    tag: string,
                    textareaId: string
                  ) => {
                    const textarea = document.getElementById(textareaId) as HTMLTextAreaElement | null;
                    const currentValue = String((smsSettings.templates as any)[fieldKey] || '');
                    
                    if (textarea) {
                      const start = textarea.selectionStart ?? currentValue.length;
                      const end = textarea.selectionEnd ?? currentValue.length;
                      const updated = currentValue.substring(0, start) + tag + currentValue.substring(end);
                      
                      const newTemplates = {
                        ...smsSettings.templates,
                        [fieldKey]: updated,
                        ...(fieldKey === 'ticketApprovalWaiting' ? { ticketPriceApproved: updated } : {}),
                        ...(fieldKey === 'ticketPriceApproved' ? { ticketApprovalWaiting: updated } : {}),
                      };
                      handleUpdateSmsSettings({ templates: newTemplates });

                      // Restore focus and cursor position after insertion
                      setTimeout(() => {
                        textarea.focus();
                        textarea.setSelectionRange(start + tag.length, start + tag.length);
                      }, 0);
                    } else {
                      const updated = currentValue ? `${currentValue} ${tag}` : tag;
                      const newTemplates = {
                        ...smsSettings.templates,
                        [fieldKey]: updated,
                        ...(fieldKey === 'ticketApprovalWaiting' ? { ticketPriceApproved: updated } : {}),
                        ...(fieldKey === 'ticketPriceApproved' ? { ticketApprovalWaiting: updated } : {}),
                      };
                      handleUpdateSmsSettings({ templates: newTemplates });
                    }
                  };

                  const templateConfigs: Array<{
                    key: keyof typeof smsSettings.templates;
                    id: string;
                    title: string;
                    value: string;
                    onChange: (val: string) => void;
                  }> = [
                    {
                      key: 'ticketCreated',
                      id: 'sms_tpl_created',
                      title: '1. Kayıt Alındığında Gönderilecek Şablon',
                      value: smsSettings.templates.ticketCreated || '',
                      onChange: (val) =>
                        handleUpdateSmsSettings({
                          templates: { ...smsSettings.templates, ticketCreated: val },
                        }),
                    },
                    {
                      key: 'ticketApprovalWaiting',
                      id: 'sms_tpl_approval',
                      title: '2. Onay Bekliyor / Fiyat Onayı Şablonu',
                      value:
                        smsSettings.templates.ticketApprovalWaiting ||
                        smsSettings.templates.ticketPriceApproved ||
                        '',
                      onChange: (val) =>
                        handleUpdateSmsSettings({
                          templates: {
                            ...smsSettings.templates,
                            ticketApprovalWaiting: val,
                            ticketPriceApproved: val,
                          },
                        }),
                    },
                    {
                      key: 'ticketReady',
                      id: 'sms_tpl_ready',
                      title: '3. Teslimata Hazır Şablonu',
                      value: smsSettings.templates.ticketReady || '',
                      onChange: (val) =>
                        handleUpdateSmsSettings({
                          templates: { ...smsSettings.templates, ticketReady: val },
                        }),
                    },
                    {
                      key: 'ticketDelivered',
                      id: 'sms_tpl_delivered',
                      title: '4. Teslim Edildi Şablonu',
                      value: smsSettings.templates.ticketDelivered || '',
                      onChange: (val) =>
                        handleUpdateSmsSettings({
                          templates: { ...smsSettings.templates, ticketDelivered: val },
                        }),
                    },
                    {
                      key: 'ticketCancelled',
                      id: 'sms_tpl_cancelled',
                      title: '5. İptal / İade Edildi Şablonu',
                      value: smsSettings.templates.ticketCancelled || '',
                      onChange: (val) =>
                        handleUpdateSmsSettings({
                          templates: { ...smsSettings.templates, ticketCancelled: val },
                        }),
                    },
                  ];

                  return (
                    <div className="space-y-4">
                      {templateConfigs.map((tpl) => {
                        const len = tpl.value.length;
                        return (
                          <div
                            key={tpl.key}
                            className="bg-slate-50/80 border border-slate-200 rounded-xl p-3.5 sm:p-4 space-y-2.5 transition-all hover:border-slate-300"
                          >
                            <div className="flex items-center justify-between gap-1.5">
                              <label
                                htmlFor={tpl.id}
                                className="text-xs font-bold text-slate-800 cursor-pointer"
                              >
                                {tpl.title}
                              </label>
                              <div className="text-[11px] font-mono text-slate-500">
                                <span>{len} karakter</span>
                              </div>
                            </div>

                            {/* Placeholder Insertion Button Toolbar */}
                            <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                              <span className="text-[10px] font-bold text-slate-500 mr-1">Ekle:</span>
                              {placeholders.map((p) => (
                                <button
                                  type="button"
                                  key={p.tag}
                                  onClick={() => insertTag(tpl.key, p.tag, tpl.id)}
                                  title={`${p.label} (${p.desc}) ekle`}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-white hover:bg-blue-50 text-blue-700 hover:text-blue-800 border border-slate-200 hover:border-blue-300 rounded-lg text-xs font-mono font-bold shadow-2xs transition-all cursor-pointer"
                                >
                                  <span>{p.tag}</span>
                                </button>
                              ))}
                            </div>

                            {/* Expanded Textarea */}
                            <textarea
                              id={tpl.id}
                              rows={3}
                              value={tpl.value}
                              onChange={(e) => tpl.onChange(e.target.value)}
                              placeholder={`${tpl.title} metnini yazınız veya yukarıdaki butonlardan değişken ekleyiniz...`}
                              className="w-full p-3 text-xs leading-relaxed border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white font-mono shadow-2xs resize-y"
                            />
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Test SMS Sender Box */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                <h4 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                  <Send className="w-3.5 h-3.5 text-blue-600" />
                  <span>Canlı Test SMS Gönderimi</span>
                </h4>
                <div className="flex flex-col sm:flex-row items-center gap-2">
                  <input
                    type="text"
                    value={testPhone}
                    onChange={e => setTestPhone(e.target.value)}
                    placeholder="Test edilecek telefon no (Örn: 05051234567)"
                    className="w-full sm:flex-1 px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-mono"
                  />
                  <button
                    type="button"
                    onClick={handleSendTestSms}
                    disabled={sendingTestSms}
                    className="w-full sm:w-auto px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold shadow-xs flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Send className={`w-3 h-3 ${sendingTestSms ? 'animate-spin' : ''}`} />
                    <span>{sendingTestSms ? 'Gönderiliyor...' : 'Test SMS Gönder'}</span>
                  </button>
                </div>

                {testSmsStatus && (
                  <p
                    className={`text-xs font-semibold p-2 rounded-lg ${
                      testSmsStatus.type === 'success'
                        ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                        : 'bg-rose-50 text-rose-800 border border-rose-200'
                    }`}
                  >
                    {testSmsStatus.text}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* TAB 3: SECURITY & ADMIN PASSWORD */}
          {activeTab === 'security' && isCurrentLoggedInMasterAdmin && (
            <div className="space-y-4">
              {/* Copied Notice Banner */}
              {copiedNotice && (
                <div className="p-3 bg-emerald-50 border border-emerald-300 rounded-xl text-emerald-800 text-xs font-semibold flex items-center space-x-2 animate-in fade-in">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>{copiedNotice}</span>
                </div>
              )}

              <div className="bg-slate-50 border border-slate-200/90 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600">
                      <KeyRound className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-900">Yönetici (Admin) Giriş Bilgileri</h4>
                      <p className="text-[11px] text-slate-500">
                        Uygulamaya girerken istenecek ana yönetici kullanıcı adı ve şifrenizi buradan yönetin.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const textToCopy = `Kullanıcı Adı: ${adminUsername}\nŞifre: ${adminPassword}\nGüvenlik Cevabı: ${securityAnswer}`;
                      navigator.clipboard.writeText(textToCopy);
                      setCopiedNotice('Giriş bilgileri panoya kopyalandı!');
                      setTimeout(() => setCopiedNotice(null), 3000);
                    }}
                    className="px-2.5 py-1.5 bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 text-xs font-bold rounded-lg flex items-center space-x-1 shadow-2xs cursor-pointer transition-colors"
                  >
                    <Copy className="w-3.5 h-3.5 text-slate-500" />
                    <span>Bilgileri Kopyala</span>
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">Kullanıcı Adı</label>
                    <div className="relative">
                      <User className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        required
                        value={adminUsername}
                        onChange={e => setAdminUsername(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-xs font-semibold border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">Giriş Şifresi</label>
                    <div className="relative">
                      <Lock className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                      <input
                        type={showAdminPassword ? 'text' : 'password'}
                        required
                        value={adminPassword}
                        onChange={e => setAdminPassword(e.target.value)}
                        className="w-full pl-8 pr-10 py-1.5 text-xs font-mono font-bold border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                      />
                      <button
                        type="button"
                        onClick={() => setShowAdminPassword(!showAdminPassword)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 cursor-pointer"
                        title={showAdminPassword ? 'Şifreyi Gizle' : 'Şifreyi Göster'}
                      >
                        {showAdminPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-200/60 space-y-2">
                  <label className="block text-[11px] font-bold text-slate-800">
                    Şifre Kurtarma Güvenlik Sorusu &amp; Yanıtı
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <input
                        type="text"
                        placeholder="Güvenlik Sorusu / Kriteri"
                        value={securityQuestion}
                        onChange={e => setSecurityQuestion(e.target.value)}
                        className="w-full px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                      />
                    </div>
                    <div className="relative">
                      <input
                        type={showSecurityAnswer ? 'text' : 'password'}
                        placeholder="Gizli Cevap veya Kurtarma PIN'i"
                        value={securityAnswer}
                        onChange={e => setSecurityAnswer(e.target.value)}
                        className="w-full pl-3 pr-10 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSecurityAnswer(!showSecurityAnswer)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 cursor-pointer"
                        title={showSecurityAnswer ? 'Cevabı Gizle' : 'Cevabı Göster'}
                      >
                        {showSecurityAnswer ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sunucu ve MySQL Veritabanı Yedekleme Kartı */}
              <div className="bg-slate-50 border border-slate-200/90 rounded-xl p-4 space-y-4">
                <div className="flex items-center justify-between border-b border-slate-200/80 pb-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600">
                      <Database className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-900">Veritabanı &amp; MySQL / phpMyAdmin Yedekleme</h4>
                      <p className="text-[11px] text-slate-500">
                        Verilerinizi phpMyAdmin için tam MySQL SQL dökümü veya JSON yedek formatında güvenle dışa aktarın / geri yükleyin.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setActiveTab('database')}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-2xs transition-colors cursor-pointer"
                    >
                      <Table className="w-3.5 h-3.5" />
                      <span>Tabloları &amp; Kayıtları İncele</span>
                    </button>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                      <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                      <span>Otomatik Eşzamanlama Aktif</span>
                    </span>
                  </div>
                </div>

                {restoreMessage && (
                  <div className={`p-3 rounded-xl text-xs font-semibold flex items-center space-x-2 animate-in fade-in ${
                    restoreMessage.type === 'success'
                      ? 'bg-emerald-50 border border-emerald-300 text-emerald-800'
                      : 'bg-rose-50 border border-rose-300 text-rose-800'
                  }`}>
                    {restoreMessage.type === 'success' ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                    )}
                    <span>{restoreMessage.text}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                  {/* 1. MySQL SQL Export */}
                  <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs flex flex-col justify-between space-y-3">
                    <div>
                      <div className="flex items-center space-x-1.5 text-blue-600 mb-1">
                        <Table className="w-4 h-4" />
                        <h5 className="text-xs font-bold text-slate-900">MySQL / phpMyAdmin (.sql)</h5>
                      </div>
                      <p className="text-[11px] text-slate-500 leading-snug">
                        phpMyAdmin veya MySQL veritabanına tek tıkla aktarabileceğiniz CREATE TABLE ve INSERT dökümünü indirin.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleDownloadSql}
                      disabled={downloadingSql}
                      className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg text-xs font-bold shadow-xs flex items-center justify-center space-x-1.5 cursor-pointer transition-colors"
                    >
                      <Download className={`w-3.5 h-3.5 ${downloadingSql ? 'animate-bounce' : ''}`} />
                      <span>{downloadingSql ? 'Oluşturuluyor...' : 'MySQL SQL İndir'}</span>
                    </button>
                  </div>

                  {/* 2. Full JSON Backup */}
                  <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs flex flex-col justify-between space-y-3">
                    <div>
                      <div className="flex items-center space-x-1.5 text-indigo-600 mb-1">
                        <FileCode className="w-4 h-4" />
                        <h5 className="text-xs font-bold text-slate-900">Tam JSON Yedeği (.json)</h5>
                      </div>
                      <p className="text-[11px] text-slate-500 leading-snug">
                        Tüm servis kayıtları, stoklar, kullanıcılar ve şirket ayarlarını içeren taşınabilir yedek dosyası.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={downloadServerDataJson}
                      className="w-full px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold shadow-xs flex items-center justify-center space-x-1.5 cursor-pointer transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" />
                      <span>JSON Yedeği İndir</span>
                    </button>
                  </div>

                  {/* 3. Restore Backup */}
                  <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs flex flex-col justify-between space-y-3">
                    <div>
                      <div className="flex items-center space-x-1.5 text-amber-600 mb-1">
                        <Upload className="w-4 h-4" />
                        <h5 className="text-xs font-bold text-slate-900">Yedekten Geri Yükle</h5>
                      </div>
                      <p className="text-[11px] text-slate-500 leading-snug">
                        Daha önce indirdiğiniz JSON yedek dosyasını seçerek tüm verileri sisteme geri yükleyin.
                      </p>
                    </div>
                    <label className={`w-full px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold shadow-xs flex items-center justify-center space-x-1.5 cursor-pointer transition-colors ${restoringBackup ? 'opacity-50 pointer-events-none' : ''}`}>
                      <Upload className="w-3.5 h-3.5" />
                      <span>{restoringBackup ? 'Yükleniyor...' : 'Yedek Dosyası Seç'}</span>
                      <input
                        type="file"
                        accept=".json"
                        onChange={handleRestoreFile}
                        className="hidden"
                        disabled={restoringBackup}
                      />
                    </label>
                  </div>
                </div>

                <div className="p-3 bg-blue-50/70 border border-blue-200/80 rounded-xl text-[11px] text-blue-900 flex items-start space-x-2">
                  <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="font-semibold">Çoklu Tarayıcı &amp; Cihaz Eşitleme Güvencesi:</p>
                    <p className="text-blue-800">
                      Sistem her işlemde sunucu veritabanı ile çift yönlü doğrulamalı senkronizasyon yapar. Farklı tarayıcılarda şifre değişimi uyarısı almadan kesintisiz çalışabilir, alınan SQL yedeğini doğrudan cPanel / Plesk phpMyAdmin arayüzündeki <strong>İçe Aktar (Import)</strong> sekmesinden tek tıkla yükleyebilirsiniz.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: USERS & TECHNICIANS */}
          {activeTab === 'users' && (
            <div className="space-y-4">
              {/* Status Message */}
              {userSuccessMessage && (
                <div className="p-3 bg-emerald-50 border border-emerald-300 rounded-xl text-emerald-800 text-xs font-semibold flex items-center space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>{userSuccessMessage}</span>
                </div>
              )}

              {/* Header & Add Button */}
              <div className="flex items-center justify-between pt-1">
                <div>
                  <h3 className="text-xs font-bold text-slate-800">Personel &amp; Kullanıcı Hesapları</h3>
                  <p className="text-[11px] text-slate-500">Sisteme giriş yapabilecek yönetici ve teknisyen hesaplarını yönetin</p>
                </div>
                {editingUserId === null && (
                  <button
                    type="button"
                    onClick={handleOpenAddUser}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold flex items-center space-x-1.5 shadow-sm shadow-blue-600/30 transition-all cursor-pointer"
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    <span>Yeni Kullanıcı Ekle</span>
                  </button>
                )}
              </div>

              {/* Add / Edit Form Card */}
              {editingUserId !== null && (() => {
                const targetUser = editingUserId !== 'new' ? usersList.find(u => u.id === editingUserId) : null;
                const isEditingMasterAdmin =
                  isMasterAdminUser(targetUser) ||
                  userFormUsername.trim().toLowerCase() === 'admin' ||
                  userFormUsername.trim().toLowerCase() === (companyInfo.adminUsername || 'admin').toLowerCase() ||
                  editingUserId === 'usr_admin';

                return (
                <div className="bg-slate-50 border-2 border-blue-200 rounded-xl p-4 space-y-3.5">
                  <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                    <h4 className="text-xs font-bold text-blue-900 flex items-center gap-1.5">
                      {editingUserId === 'new' ? <UserPlus className="w-4 h-4 text-blue-600" /> : <Edit2 className="w-4 h-4 text-blue-600" />}
                      <span>{editingUserId === 'new' ? 'Yeni Personel / Teknisyen Ekle' : isEditingMasterAdmin ? 'Ana Yönetici Bilgilerini Düzenle' : 'Kullanıcı Hesabını Düzenle'}</span>
                    </h4>
                    <button
                      type="button"
                      onClick={() => setEditingUserId(null)}
                      className="text-slate-400 hover:text-slate-700 text-xs font-semibold cursor-pointer"
                    >
                      Kapat
                    </button>
                  </div>

                  {userFormError && (
                    <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-lg text-rose-700 text-xs font-medium flex items-center space-x-2">
                      <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                      <span>{userFormError}</span>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 mb-1">Adı Soyadı *</label>
                      <input
                        type="text"
                        placeholder="Örn: Ahmet Usta"
                        value={userFormName}
                        onChange={e => setUserFormName(e.target.value)}
                        className="w-full px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-[11px] font-bold text-slate-700">Kullanıcı Adı (Giriş İçin) *</label>
                      </div>
                      <input
                        type="text"
                        placeholder="Örn: ahmet123"
                        value={userFormUsername}
                        disabled={isEditingMasterAdmin}
                        onChange={e => !isEditingMasterAdmin && setUserFormUsername(e.target.value)}
                        className={`w-full px-3 py-1.5 text-xs border rounded-lg font-mono ${
                          isEditingMasterAdmin
                            ? 'bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed select-none'
                            : 'border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white'
                        }`}
                      />
                    </div>

                    {!isEditingMasterAdmin && (
                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 mb-1">{editingUserId === 'new' ? 'Giriş Şifresi *' : 'Yeni Giriş Şifresi'}</label>
                        <div className="relative">
                          <input
                            type={showAdminPassword ? 'text' : 'password'}
                            placeholder={editingUserId === 'new' ? 'En az 3 karakter' : 'Değiştirmek için yeni şifre girin'}
                            value={userFormPassword}
                            onChange={e => setUserFormPassword(e.target.value)}
                            className="w-full pl-3 pr-9 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white font-mono font-bold"
                          />
                          <button
                            type="button"
                            onClick={() => setShowAdminPassword(!showAdminPassword)}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 cursor-pointer"
                          >
                            {showAdminPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 mb-1">Telefon Numarası (Opsiyonel)</label>
                      <input
                        type="text"
                        placeholder="05XX XXX XX XX"
                        value={userFormPhone}
                        onChange={e => setUserFormPhone(e.target.value)}
                        className="w-full px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                      />
                    </div>
                  </div>

                  {/* Rol Seçimi */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-[11px] font-bold text-slate-700">Kullanıcı Rolü &amp; Yetki Seviyesi</label>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      <div
                        onClick={() => {
                          if (isEditingMasterAdmin) {
                            setUserFormError('Ana yönetici (admin) hesabının rolü teknisyen olarak değiştirilemez.');
                            return;
                          }
                          setUserFormRole('technician');
                        }}
                        className={`p-3 rounded-xl border-2 transition-all ${
                          isEditingMasterAdmin
                            ? 'bg-slate-50 border-slate-200 text-slate-400 opacity-60 cursor-not-allowed select-none'
                            : userFormRole === 'technician'
                            ? 'bg-amber-500/10 border-amber-500 text-amber-950 shadow-xs cursor-pointer'
                            : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700 cursor-pointer'
                        }`}
                      >
                        <div className="flex items-center justify-between font-bold text-xs">
                          <div className="flex items-center space-x-2">
                            <Wrench className="w-4 h-4 text-amber-600" />
                            <span>Teknisyen</span>
                          </div>
                          {isEditingMasterAdmin && (
                            <span className="text-[9px] text-rose-600 font-bold bg-rose-50 px-1.5 py-0.5 rounded border border-rose-200">
                              Değiştirilemez
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                          Servis onarım, parça ve atanan kayıtlarla ilgilenir. Firma ve SMS ayarlarını göremez.
                        </p>
                      </div>

                      <div
                        onClick={() => {
                          if (isEditingMasterAdmin) {
                            setUserFormError('Ana yönetici (admin) hesabının rolü karşılama olarak değiştirilemez.');
                            return;
                          }
                          setUserFormRole('limited_technician');
                        }}
                        className={`p-3 rounded-xl border-2 transition-all ${
                          isEditingMasterAdmin
                            ? 'bg-slate-50 border-slate-200 text-slate-400 opacity-60 cursor-not-allowed select-none'
                            : userFormRole === 'limited_technician'
                            ? 'bg-purple-500/10 border-purple-500 text-purple-950 shadow-xs cursor-pointer'
                            : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700 cursor-pointer'
                        }`}
                      >
                        <div className="flex items-center justify-between font-bold text-xs">
                          <div className="flex items-center space-x-2">
                            <UserCheck className="w-4 h-4 text-purple-600" />
                            <span>Karşılama</span>
                          </div>
                          {isEditingMasterAdmin && (
                            <span className="text-[9px] text-rose-600 font-bold bg-rose-50 px-1.5 py-0.5 rounded border border-rose-200">
                              Değiştirilemez
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                          Sadece yeni kayıt açar, teslim/iade edip ödeme tutarını girer ve raporları alır. İşlem/parça ekleyemez, silemez, alt servise gönderemez.
                        </p>
                      </div>

                      <div
                        onClick={() => setUserFormRole('admin')}
                        className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                          userFormRole === 'admin'
                            ? 'bg-blue-500/10 border-blue-500 text-blue-950 shadow-xs'
                            : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'
                        }`}
                      >
                        <div className="flex items-center space-x-2 font-bold text-xs">
                          <ShieldCheck className="w-4 h-4 text-blue-600" />
                          <span>Yönetici</span>
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                          Firma ayarları, SMS entegrasyonu ve tüm sisteme tam erişim ve yönetim hakkına sahiptir.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Aktif / Pasif */}
                  {isEditingMasterAdmin ? (
                    <div className="flex items-center space-x-2 pt-1">
                      <input
                        type="checkbox"
                        id="userIsActive"
                        checked={true}
                        disabled={true}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 opacity-60 cursor-not-allowed"
                      />
                      <label htmlFor="userIsActive" className="text-xs font-semibold text-slate-700 select-none flex items-center gap-1.5">
                        <span>Ana yönetici hesabı her zaman aktiftir (Pasife alınamaz)</span>
                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-200">Aktif</span>
                      </label>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2 pt-1">
                      <input
                        type="checkbox"
                        id="userIsActive"
                        checked={userFormIsActive}
                        onChange={e => setUserFormIsActive(e.target.checked)}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <label htmlFor="userIsActive" className="text-xs font-semibold text-slate-700 cursor-pointer">
                        Bu kullanıcı hesabı aktif ve sisteme giriş yapabilir
                      </label>
                    </div>
                  )}

                  {/* Form Action Buttons */}
                  <div className="flex justify-end space-x-2 pt-2 border-t border-slate-200">
                    <button
                      type="button"
                      onClick={() => setEditingUserId(null)}
                      className="px-3 py-1.5 border border-slate-300 text-slate-600 hover:bg-slate-100 rounded-lg text-xs font-semibold cursor-pointer"
                    >
                      Vazgeç
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveUserForm}
                      className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold flex items-center space-x-1.5 shadow-sm cursor-pointer"
                    >
                      <Check className="w-3.5 h-3.5" />
                      <span>{editingUserId === 'new' ? 'Kullanıcıyı Kaydet' : 'Değişiklikleri Kaydet'}</span>
                    </button>
                  </div>
                </div>
                );
              })()}

              {/* User List Table */}
              <div className="border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
                <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
                  <thead className="bg-slate-100/80 font-bold text-slate-700">
                    <tr>
                      <th className="px-3.5 py-2.5">Kullanıcı</th>
                      <th className="px-3.5 py-2.5">Kullanıcı Adı / Şifre</th>
                      <th className="px-3.5 py-2.5">Rol / Yetki</th>
                      <th className="px-3.5 py-2.5">Durum</th>
                      <th className="px-3.5 py-2.5 text-right">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {usersList.map(user => {
                      const isTech = user.role === 'technician';
                      const isLimited = user.role === 'limited_technician';
                      const isTargetMaster = isMasterAdminUser(user);
                      const isHiddenFromSecondaryAdmin = isTargetMaster && !isCurrentLoggedInMasterAdmin;

                      return (
                        <tr key={user.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="px-3.5 py-2.5">
                            <div className="flex items-center space-x-2.5">
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${
                                isTech
                                  ? 'bg-amber-100 text-amber-800'
                                  : isLimited
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-blue-100 text-blue-800'
                              }`}>
                                {isTech ? (
                                  <Wrench className="w-3.5 h-3.5" />
                                ) : isLimited ? (
                                  <UserCheck className="w-3.5 h-3.5" />
                                ) : (
                                  <ShieldCheck className="w-3.5 h-3.5" />
                                )}
                              </div>
                              <div>
                                <div className="font-bold text-slate-800">
                                  {isHiddenFromSecondaryAdmin ? 'Ana Sistem Yöneticisi' : user.name}
                                </div>
                                {!isHiddenFromSecondaryAdmin && user.phone && (
                                  <div className="text-[10px] text-slate-500">{user.phone}</div>
                                )}
                              </div>
                            </div>
                          </td>

                          <td className="px-3.5 py-2.5 font-mono">
                            <div className="text-slate-800 font-semibold text-xs">
                              {isHiddenFromSecondaryAdmin ? 'Ana Yönetici (Korumalı)' : user.username}
                            </div>
                            {!isHiddenFromSecondaryAdmin ? (
                              <div className="flex items-center space-x-1.5 mt-0.5">
                                <span className="text-[11px] text-slate-700 font-bold bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                                  {visibleUserPasswordIds[user.id] ? 'Hash korumalı — görüntülenemez' : '••••••'}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setVisibleUserPasswordIds(prev => ({ ...prev, [user.id]: !prev[user.id] }))}
                                  className="text-slate-400 hover:text-slate-700 p-0.5 rounded cursor-pointer"
                                  title={visibleUserPasswordIds[user.id] ? 'Bilgiyi Gizle' : 'Parola durumunu göster'}
                                >
                                  {visibleUserPasswordIds[user.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                </button>
                              </div>
                            ) : (
                              <div className="text-[10px] text-slate-500 font-bold tracking-wider">••••••</div>
                            )}
                          </td>

                          <td className="px-3.5 py-2.5">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              isTech
                                ? 'bg-amber-100 text-amber-800 border border-amber-300'
                                : isLimited
                                ? 'bg-purple-100 text-purple-800 border border-purple-300'
                                : 'bg-blue-100 text-blue-800 border border-blue-300'
                            }`}>
                              {isTech ? 'Teknisyen' : isLimited ? 'Karşılama' : 'Yönetici'}
                            </span>
                          </td>

                          <td className="px-3.5 py-2.5">
                            {isTargetMaster ? (
                              <span
                                title="Ana yönetici hesabı sistem güvenliği ve erişim sürekliliği için her zaman aktiftir"
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 select-none cursor-default"
                              >
                                <span className="w-1.5 h-1.5 rounded-full mr-1.5 bg-emerald-500" />
                                Aktif
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleToggleUserStatus(user)}
                                title="Durumu Değiştirmek İçin Tıklayın"
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold cursor-pointer transition-all ${
                                  user.isActive !== false
                                    ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                                    : 'bg-rose-100 text-rose-800 hover:bg-rose-200'
                                }`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${user.isActive !== false ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                                {user.isActive !== false ? 'Aktif' : 'Pasif'}
                              </button>
                            )}
                          </td>

                          <td className="px-3.5 py-2.5 text-right space-x-1 whitespace-nowrap">
                            {isHiddenFromSecondaryAdmin ? (
                              <span className="text-[10px] text-slate-400 font-semibold px-2 py-0.5 bg-slate-100 rounded-md select-none">
                                Korumalı
                              </span>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleOpenEditUser(user)}
                                  title="Düzenle"
                                  className="p-1 text-slate-500 hover:text-blue-600 hover:bg-slate-100 rounded-md transition-colors cursor-pointer"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>

                                {!isTargetMaster && (
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteUser(user)}
                                    title="Kullanıcıyı Sil"
                                    className="p-1 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors cursor-pointer"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 5: DEVICES (CİHAZLAR - TÜRLER VE MARKALAR) */}
          {activeTab === 'devices' && (
            <div className="space-y-6">
              {/* Notification Banner */}
              {deviceSuccessMsg && (
                <div className="p-3 bg-emerald-50 border border-emerald-300 rounded-xl text-emerald-800 text-xs font-semibold flex items-center space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>{deviceSuccessMsg}</span>
                </div>
              )}
              {deviceErrorMsg && (
                <div className="p-3 bg-rose-50 border border-rose-300 rounded-xl text-rose-800 text-xs font-semibold flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                  <span>{deviceErrorMsg}</span>
                </div>
              )}

              {/* Info banner */}
              <div className="p-3.5 bg-blue-50 border border-blue-200 rounded-xl flex items-start space-x-2.5">
                <Smartphone className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                <div className="text-xs text-blue-900 leading-relaxed">
                  <span className="font-bold">Cihaz Türleri &amp; Marka Listesi:</span> Yeni servis kaydı oluştururken cihaz türü açılır menüsünde ve marka otomatik tamamlama listesinde görünen kayıtları buradan ekleyebilir veya silebilirsiniz.
                </div>
              </div>

              {/* Section 1: Cihaz Türleri */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Smartphone className="w-4 h-4 text-blue-600" />
                    <div>
                      <h4 className="text-xs font-bold text-slate-800">Cihaz Türleri</h4>
                      <p className="text-[11px] text-slate-500">Servis kaydı açılırken seçilen cihaz kategorileri ({deviceTypesList.length} adet)</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleResetDeviceTypes}
                    className="text-[11px] text-slate-500 hover:text-blue-600 font-semibold flex items-center gap-1 hover:underline cursor-pointer"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>Varsayılana Sıfırla</span>
                  </button>
                </div>

                {/* Add new device type input */}
                <div className="flex items-center gap-2 pt-1">
                  <input
                    type="text"
                    placeholder="Yeni cihaz türü adı (Örn: Akıllı Saat, Yazıcı, Drone...)"
                    value={newTypeName}
                    onChange={e => setNewTypeName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddDeviceType();
                      }
                    }}
                    className="flex-1 px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                  />
                  <button
                    type="button"
                    onClick={handleAddDeviceType}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold flex items-center space-x-1.5 shadow-sm shadow-blue-600/30 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Tür Ekle</span>
                  </button>
                </div>

                {/* Device types pills / grid */}
                <div className="flex flex-wrap gap-2 pt-2">
                  {deviceTypesList.map((type) => (
                    <div
                      key={type}
                      className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-800 shadow-xs group hover:border-slate-300 transition-colors"
                    >
                      <Smartphone className="w-3.5 h-3.5 text-blue-500" />
                      <span>{type}</span>
                      <button
                        type="button"
                        onClick={() => handleDeleteDeviceType(type)}
                        title={`"${type}" türünü sil`}
                        className="ml-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded p-0.5 transition-colors cursor-pointer"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Cihaz alanı atamaları */}
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="mb-3">
                    <h5 className="text-xs font-black text-slate-800">Cihaz Alanı Ataması</h5>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Her cihaz türünü <b>Yazıcı / Tarayıcı</b> veya <b>Bilgisayar</b> alanına ata. Raporlar bu atamaya göre ayrılır.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {deviceTypesList.map(type => {
                      const lower = type.toLocaleLowerCase('tr-TR');
                      const inferred = lower.includes('yazıcı') || lower.includes('yazici') || lower.includes('tarayıcı') || lower.includes('tarayici') || lower.includes('printer') || lower.includes('scanner') || lower.includes('fotokopi')
                        ? 'printer'
                        : 'computer';
                      const selected = deviceAreaAssignments[type] || inferred;
                      return (
                        <div key={`area-${type}`} className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2">
                          <span className="text-xs font-bold text-slate-700 truncate">{type}</span>
                          <select
                            value={selected}
                            onChange={e => setDeviceAreaAssignments(prev => ({
                              ...prev,
                              [type]: e.target.value as 'printer' | 'computer',
                            }))}
                            className="text-xs font-bold border border-slate-300 rounded-lg px-2 py-1.5 bg-white text-slate-700 outline-none focus:ring-2 focus:ring-blue-500/20"
                          >
                            <option value="printer">Yazıcı / Tarayıcı</option>
                            <option value="computer">Bilgisayar</option>
                          </select>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-2">
                    Bu alan bir prim ayarı değildir; yalnızca servis ve gelir raporlarını iki iş alanına ayırır.
                  </p>
                </div>
              </div>

              {/* Section 2: Cihaz Markaları */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Tag className="w-4 h-4 text-emerald-600" />
                    <div>
                      <h4 className="text-xs font-bold text-slate-800">Kayıtlı Markalar</h4>
                      <p className="text-[11px] text-slate-500">Kayıt açarken otomatik tamamlanan marka listesi ({deviceBrandsList.length} kayıtlı marka)</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleResetDeviceBrands}
                    className="text-[11px] text-slate-500 hover:text-emerald-600 font-semibold flex items-center gap-1 hover:underline cursor-pointer"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>Varsayılana Sıfırla</span>
                  </button>
                </div>

                {/* Add new brand input */}
                <div className="flex items-center gap-2 pt-1">
                  <input
                    type="text"
                    placeholder="Yeni marka adı (Örn: Apple, Samsung, Lenovo, Asus...)"
                    value={newBrandName}
                    onChange={e => setNewBrandName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddDeviceBrand();
                      }
                    }}
                    className="flex-1 px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-white"
                  />
                  <button
                    type="button"
                    onClick={handleAddDeviceBrand}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-bold flex items-center space-x-1.5 shadow-sm shadow-emerald-600/30 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Marka Ekle</span>
                  </button>
                </div>

                {/* Search filter for brands */}
                {deviceBrandsList.length > 8 && (
                  <div className="relative pt-1">
                    <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-3" />
                    <input
                      type="text"
                      placeholder="Markalar arasında ara..."
                      value={brandSearchQuery}
                      onChange={e => setBrandSearchQuery(e.target.value)}
                      className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                    />
                  </div>
                )}

                {/* Device brands badges / grid */}
                <div className="flex flex-wrap gap-1.5 pt-1 max-h-56 overflow-y-auto p-1">
                  {deviceBrandsList
                    .filter(b => b.toLowerCase().includes(brandSearchQuery.trim().toLowerCase()))
                    .map((brand) => (
                      <div
                        key={brand}
                        className="inline-flex items-center space-x-1.5 px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-800 shadow-xs hover:border-slate-300 transition-colors"
                      >
                        <Tag className="w-3 h-3 text-emerald-500" />
                        <span>{brand}</span>
                        <button
                          type="button"
                          onClick={() => handleDeleteDeviceBrand(brand)}
                          title={`"${brand}" markasını sil`}
                          className="ml-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded p-0.5 transition-colors cursor-pointer"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  {deviceBrandsList.filter(b => b.toLowerCase().includes(brandSearchQuery.trim().toLowerCase())).length === 0 && (
                    <div className="w-full text-center py-4 text-xs text-slate-400">
                      Aramanıza uygun marka bulunamadı.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB 6: DATABASE & TABLE STATS MONITOR */}
          {activeTab === 'database' && (
            <div className="space-y-4">
              <DatabaseTableMonitor />
            </div>
          )}

          {/* Modal Footer */}
          <div className="pt-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
            <button
              type="button"
              onClick={handleTestDatabaseConnection}
              disabled={isTestingDb}
              className="w-full sm:w-auto px-4 py-2 bg-slate-100 hover:bg-blue-50 text-slate-700 hover:text-blue-700 border border-slate-300 hover:border-blue-300 rounded-lg text-xs font-bold flex items-center justify-center space-x-1.5 transition-colors cursor-pointer disabled:opacity-60 shadow-2xs"
            >
              <Database className="w-3.5 h-3.5 text-blue-600" />
              {isTestingDb ? (
                <RefreshCw className="w-3.5 h-3.5 animate-spin text-blue-600" />
              ) : null}
              <span>{isTestingDb ? 'Veritabanı Test Ediliyor...' : 'Veritabanı Test Et'}</span>
            </button>

            <div className="flex items-center space-x-2 w-full sm:w-auto justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-100 text-xs font-bold transition-colors cursor-pointer"
              >
                İptal
              </button>

              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold shadow-md shadow-blue-600/30 flex items-center space-x-2 transition-all hover:scale-[1.02] cursor-pointer"
              >
                <Check className="w-4 h-4" />
                <span>Tüm Ayarları Kaydet</span>
              </button>
            </div>
          </div>
        </form>
      </div>
      </div>
    </div>
  );
};
